import {
  // getActivePromo,
  getItemCardTrackingParams,
  getPromoIgnoreQTY,
  trackEvent,
} from '@diamond/shared/utils';
import { TRACKING_EVENTS } from '@diamond/sol-buyer/constant';
import { useCallback, useEffect, useRef, useState } from 'react';

export interface TrackingProduct {
  id: string;
  item_code?: number;
  title?: string;
  division?: 'trading' | 'diamond' | 'equipment' | 'fnb';
  brand_name?: string;
  brand_desc?: string;
  desc?: string;
  type?: string;
  pack_unit?: string;
  information?: string;
  discount_promos?: unknown[];
  prices: Array<{ price: number }>;
  product_images_medium?: string[];
  sales_unit?: string;
  stock?: number;
  [key: string]: unknown;
}

export interface TrackingConfig {
  sectionName: string;
  pageName: string;
  listId: string;
  listName: string;
  promoSection?: {
    promo_name?: string;
    promo_code?: string;
    mechanism?: string;
  };
}

export interface ViewportTrackingOptions {
  /** Intersection threshold for section visibility (0-1) */
  intersectionThreshold?: number;
  /** Whether to track individual VIEW_ITEM events */
  trackIndividualItems?: boolean;
  /** Whether to track individual VIEW_PROMOTION events for products with promotions */
  trackPromotionItems?: boolean;
  /** Debounce time for scroll events (ms) */
  scrollDebounceTime?: number;
}

export const SCROLL_DEBOUNCE_TIME = 400;

/**
 * Custom hook for tracking product visibility with viewport detection
 * Handles both desktop pagination and mobile scroll tracking
 */
export function useViewportTracking<T extends TrackingProduct>(
  products: T[],
  config: TrackingConfig,
  options: ViewportTrackingOptions = {}
) {
  const {
    intersectionThreshold = 0.65,
    trackIndividualItems = true,
    trackPromotionItems = true,
    scrollDebounceTime = SCROLL_DEBOUNCE_TIME,
  } = options;

  // Refs
  const sectionRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const hasScrolledRef = useRef(false);

  // State
  const [isSectionInViewport, setIsSectionInViewport] = useState(false);
  const [hasTrackedInitialView, setHasTrackedInitialView] = useState(false);

  // Create tracking parameters for a product
  const createItemTrackingParams = useCallback(
    (product: T, index: number) => {
      return getItemCardTrackingParams(
        product as unknown as Parameters<typeof getItemCardTrackingParams>[0],
        1,
        config.sectionName,
        config.pageName,
        undefined,
        config.promoSection
      );
    },
    [config]
  );

  // Track individual item view
  const trackIndividualItem = useCallback(
    (product: T, index: number) => {
      if (!trackIndividualItems) return;

      const itemParams = createItemTrackingParams(product, index);

      trackEvent(TRACKING_EVENTS.VIEW_ITEM, {
        currency: 'IDR',
        value: product.prices.length > 0 ? product.prices[0].price : 0,
        items: [itemParams],
        item_list_id: config.listId,
        item_list_name: config.listName,
        section_name: config.sectionName,
      });
    },
    [trackIndividualItems, createItemTrackingParams, config]
  );

  // Track individual promotion view
  const trackIndividualPromotion = useCallback(
    (product: T, index: number) => {
      if (!trackPromotionItems) return;

      // Use centralized getPromoIgnoreQTY function for consistent promotion detection
      const activePromoData = getPromoIgnoreQTY(
        product as unknown as Parameters<typeof getPromoIgnoreQTY>[0]
      );

      if (!activePromoData) return;

      const itemParams = createItemTrackingParams(product, index);

      // Get the active promotion mechanism for proper tracking
      const promotionMechanism =
        activePromoData.promo.mechanism || 'Unknown Promotion';
      const promotionId = `${promotionMechanism} | ${product.item_code}`;

      trackEvent(TRACKING_EVENTS.VIEW_PROMOTION, {
        creative_name: promotionMechanism,
        creative_slot: promotionId,
        promotion_id: promotionId,
        promotion_name: promotionMechanism,
        items: [itemParams],
        item_list_id: config.listId,
        item_list_name: config.listName,
        section_name: config.sectionName,
      });
    },
    [trackPromotionItems, config, createItemTrackingParams]
  );

  // Track visible products list
  const trackVisibleProducts = useCallback(
    (visibleProducts: T[], startIndex = 0) => {
      if (visibleProducts.length === 0) return;

      const items = visibleProducts.map((product, index) => {
        trackIndividualItem(product, startIndex + index);
        trackIndividualPromotion(product, startIndex + index);
        return createItemTrackingParams(product, startIndex + index);
      });

      trackEvent(TRACKING_EVENTS.VIEW_ITEM_LIST, {
        item_list_id: config.listId,
        item_list_name: config.listName,
        items,
        section_name: config.sectionName,
        page_name: config.pageName || config.sectionName,
      });
    },
    [
      config,
      createItemTrackingParams,
      trackIndividualItem,
      trackIndividualPromotion,
    ]
  );

  // Track initial view when section becomes visible
  const trackInitialView = useCallback(
    (visibleProducts: T[], startIndex = 0, forceTrack = false) => {
      if (
        !forceTrack &&
        (hasTrackedInitialView || visibleProducts.length === 0)
      )
        return;
      if (forceTrack && visibleProducts.length === 0) return;

      trackVisibleProducts(visibleProducts, startIndex);

      // Always set to true when tracking (maintains compatibility)
      setHasTrackedInitialView(true);

      // Force viewport state when using force tracking
      if (forceTrack) {
        setIsSectionInViewport(true);
      }
    },
    [hasTrackedInitialView, trackVisibleProducts]
  );

  // Alias for force tracking (maintains backward compatibility)
  const forceTrackInitialView = useCallback(
    (visibleProducts: T[], startIndex = 0) => {
      trackInitialView(visibleProducts, startIndex, true);
    },
    [trackInitialView]
  );

  // Track pagination change (supports re-tracking per page)
  const trackPaginationChange = useCallback(
    (visibleProducts: T[], startIndex: number) => {
      if (!isSectionInViewport) return;

      // trackVisibleProducts handles both VIEW_ITEM_LIST and individual VIEW_ITEM events
      trackVisibleProducts(visibleProducts, startIndex);
    },
    [isSectionInViewport, trackVisibleProducts]
  );

  // Track mobile scroll
  const trackMobileScroll = useCallback(
    (visibleProducts: T[], startIndex: number, forceTrack = false) => {
      if (!forceTrack && (!isSectionInViewport || !hasScrolledRef.current))
        return;
      if (visibleProducts.length === 0) return;

      trackVisibleProducts(visibleProducts, startIndex);
    },
    [isSectionInViewport, trackVisibleProducts]
  );

  // Alias for force tracking (maintains backward compatibility)
  const forceTrackScrollEvents = useCallback(
    (visibleProducts: T[], startIndex: number) => {
      trackMobileScroll(visibleProducts, startIndex, true);
    },
    [trackMobileScroll]
  );

  // Setup intersection observer for section visibility with re-tracking support
  useEffect(() => {
    if (!sectionRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (
            entry.isIntersecting &&
            entry.intersectionRatio >= intersectionThreshold
          ) {
            setIsSectionInViewport(true);
          } else if (entry.intersectionRatio < 0.1) {
            // Reset viewport state when section is less than 10% visible
            // This enables re-tracking when scrolling back to the section
            setIsSectionInViewport(false);
            setHasTrackedInitialView(false);
          }
        });
      },
      {
        threshold: [0, 0.1, intersectionThreshold], // Multiple thresholds for better tracking
      }
    );

    observer.observe(sectionRef.current);

    return () => {
      observer.disconnect();
    };
  }, [intersectionThreshold]);

  // Setup mobile scroll tracking
  const setupMobileScrollTracking = useCallback(
    (
      container: HTMLDivElement,
      calculateVisibleProducts: (
        scrollLeft: number,
        clientWidth: number
      ) => {
        products: T[];
        startIndex: number;
      }
    ) => {
      let scrollTimeout: NodeJS.Timeout | null = null;

      const checkAndTrack = () => {
        const { scrollLeft, clientWidth } = container;
        const { products: visibleProducts, startIndex } =
          calculateVisibleProducts(scrollLeft, clientWidth);

        if (visibleProducts.length > 0) {
          trackMobileScroll(visibleProducts, startIndex);
        }
      };

      const handleScroll = () => {
        hasScrolledRef.current = true;
        if (scrollTimeout) clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(() => {
          checkAndTrack();
        }, scrollDebounceTime);
      };

      container.addEventListener('scroll', handleScroll);

      return () => {
        container.removeEventListener('scroll', handleScroll);
        if (scrollTimeout) clearTimeout(scrollTimeout);
      };
    },
    [trackMobileScroll, scrollDebounceTime]
  );

  // Reset scroll flag helper
  const resetScrollFlag = useCallback(() => {
    hasScrolledRef.current = false;
  }, []);

  // Set scroll flag helper
  const setScrollFlag = useCallback(() => {
    hasScrolledRef.current = true;
  }, []);

  // Force set section in viewport (for debugging)
  const forceSetSectionInViewport = useCallback(() => {
    setIsSectionInViewport(true);
  }, []);

  // Setup vertical scroll tracking for section visibility with re-tracking
  const setupVerticalScrollTracking = useCallback(
    (
      sectionElement: HTMLElement,
      calculateVisibleProducts: (sectionRect: DOMRect) => {
        products: T[];
        startIndex: number;
      },
      options: {
        visibilityThreshold?: number;
        debounceTime?: number;
        enableReTracking?: boolean;
      } = {}
    ) => {
      const {
        visibilityThreshold = 0.65,
        debounceTime = 100,
        enableReTracking = true,
      } = options;

      let scrollTimeout: NodeJS.Timeout | null = null;
      let isCurrentlyInViewport = false;

      const checkAndTrack = () => {
        if (typeof window === 'undefined') return;

        const sectionRect = sectionElement.getBoundingClientRect();
        const viewportHeight = window.innerHeight;

        // Calculate section visibility
        const visibleHeight =
          Math.min(sectionRect.bottom, viewportHeight) -
          Math.max(sectionRect.top, 0);
        const sectionVisibility = visibleHeight / sectionRect.height;

        const wasInViewport = isCurrentlyInViewport;
        isCurrentlyInViewport = sectionVisibility >= visibilityThreshold;

        // Handle section coming into view (including re-tracking when scrolling back)
        if ((!wasInViewport && isCurrentlyInViewport) || !enableReTracking) {
          const { products: visibleProducts, startIndex } =
            calculateVisibleProducts(sectionRect);

          if (visibleProducts.length > 0) {
            // Use trackVisibleProducts for direct tracking
            trackVisibleProducts(visibleProducts, startIndex);
          }
        }
      };

      const handleScroll = () => {
        hasScrolledRef.current = true;

        // Clear existing timeout to debounce
        if (scrollTimeout) clearTimeout(scrollTimeout);

        // Only fire tracking when scroll stops (debounced)
        scrollTimeout = setTimeout(() => {
          checkAndTrack();
        }, debounceTime);
      };

      // Add scroll listener to window for vertical scroll detection
      window.addEventListener('scroll', handleScroll, { passive: true });

      // Initial check when setting up
      checkAndTrack();

      return () => {
        window.removeEventListener('scroll', handleScroll);
        if (scrollTimeout) clearTimeout(scrollTimeout);
      };
    },
    [trackVisibleProducts]
  );

  // Reset tracking state (useful when products change)
  const resetTracking = useCallback(() => {
    setHasTrackedInitialView(false);
    setIsSectionInViewport(false);
    hasScrolledRef.current = false;
  }, []);

  return {
    // Refs
    sectionRef,
    containerRef,

    // State
    isSectionInViewport,
    hasTrackedInitialView,

    // Methods
    trackInitialView,
    forceTrackInitialView,
    trackPaginationChange,
    trackMobileScroll,
    forceTrackScrollEvents,
    setupMobileScrollTracking,
    setupVerticalScrollTracking,
    resetScrollFlag,
    setScrollFlag,
    forceSetSectionInViewport,
    resetTracking,

    // Direct access to tracking functions if needed
    trackVisibleProducts,
    trackIndividualItem,
  };
}
